import React from 'react';
import { ExternalLink, RotateCcw } from 'lucide-react';
import { usePageEditor } from '../components/PageContent/usePageEditor';
import {
  Section, TextField, TextAreaField, SelectField, CheckboxField, ImageField,
  RepeatableList, StringList, ICON_OPTIONS, VALUE_COLORS,
} from '../components/PageContent/Fields';
import '../components/Common/Common.css';
import '../components/PageContent/PageContent.css';

const STOREFRONT_URL = (process.env.REACT_APP_STOREFRONT_URL || 'https://tech360.ma').replace(/\/+$/, '');

function AboutPageContent() {
  const {
    content, setSection, replaceSection, loading, saving, resetting,
    message, error, setError, updatedAt, save, reset,
  } = usePageEditor('about');

  if (loading) {
    return <div className="loading">Chargement du contenu…</div>;
  }

  if (!content) {
    return (
      <div className="page-header">
        <h1>Page « À propos »</h1>
        <p className="pc-alert pc-alert-error">{error || 'Contenu indisponible.'}</p>
      </div>
    );
  }

  const hero = content.hero || {};
  const story = content.story || {};
  const values = content.values || {};
  const timeline = content.timeline || {};
  const achievements = content.achievements || {};
  const team = content.team || {};
  const cta = content.cta || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Page « À propos »</h1>
          <p className="pc-status">
            Modifiez le contenu affiché sur {STOREFRONT_URL}/about.
            {updatedAt ? ` Dernière modification : ${new Date(updatedAt).toLocaleString('fr-FR')}.` : ' Contenu d\'origine.'}
          </p>
        </div>
        <div className="pc-toolbar">
          <a
            href={`${STOREFRONT_URL}/about`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-neutral"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <ExternalLink size={15} /> Voir la page
          </a>
          <button
            type="button"
            className="btn btn-neutral"
            onClick={reset}
            disabled={resetting || saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <RotateCcw size={15} /> {resetting ? 'Restauration…' : "Restaurer l'original"}
          </button>
        </div>
      </div>

      {message && <div className="pc-alert pc-alert-success">{message}</div>}
      {error && <div className="pc-alert pc-alert-error">{error}</div>}

      {/* ---------------------------------------------------------- Hero */}
      <Section title="Bannière principale" description="Le grand titre rouge en haut de la page.">
        <TextField
          label="Titre"
          value={hero.title}
          onChange={(v) => setSection('hero', { title: v })}
        />
        <TextAreaField
          label="Sous-titre"
          value={hero.subtitle}
          onChange={(v) => setSection('hero', { subtitle: v })}
          rows={3}
        />
      </Section>

      {/* --------------------------------------------------------- Stats */}
      <Section
        title="Chiffres clés"
        description="La bande de statistiques affichée sous la bannière."
      >
        <RepeatableList
          items={content.stats}
          onChange={(items) => replaceSection('stats', items)}
          itemLabel="Chiffre"
          addLabel="Ajouter un chiffre"
          newItem={() => ({ value: '', label: '', icon: 'award' })}
          renderItem={(item, update) => (
            <div className="form-row" style={{ marginBottom: 0 }}>
              <TextField
                label="Valeur"
                value={item.value}
                onChange={(v) => update({ value: v })}
                placeholder="ex: 5000+"
              />
              <TextField
                label="Libellé"
                value={item.label}
                onChange={(v) => update({ label: v })}
                placeholder="ex: Clients satisfaits"
              />
              <SelectField
                label="Icône"
                value={item.icon}
                onChange={(v) => update({ icon: v })}
                options={ICON_OPTIONS}
              />
            </div>
          )}
        />
      </Section>

      {/* --------------------------------------------------------- Story */}
      <Section title="Notre histoire" description="Le bloc texte + image de présentation.">
        <TextField
          label="Titre de la section"
          value={story.title}
          onChange={(v) => setSection('story', { title: v })}
        />
        <StringList
          label="Paragraphes"
          items={story.paragraphs}
          onChange={(v) => setSection('story', { paragraphs: v })}
          addLabel="Ajouter un paragraphe"
          multiline
        />
        <div className="form-row">
          <ImageField
            label="Image"
            value={story.image}
            onChange={(v) => setSection('story', { image: v })}
            onError={setError}
            hint="Format conseillé : 4/3 (ex. 1200×900)"
          />
          <TextField
            label="Texte alternatif de l'image"
            value={story.image_alt}
            onChange={(v) => setSection('story', { image_alt: v })}
            hint="Décrit l'image pour Google et les lecteurs d'écran"
          />
        </div>
        <div className="form-row">
          <TextField
            label="Encadré — chiffre"
            value={story.badge_value}
            onChange={(v) => setSection('story', { badge_value: v })}
            placeholder="ex: 10+"
          />
          <TextField
            label="Encadré — texte"
            value={story.badge_text}
            onChange={(v) => setSection('story', { badge_text: v })}
          />
        </div>
      </Section>

      {/* -------------------------------------------------------- Values */}
      <Section title="Nos valeurs" description="Les quatre cartes de valeurs de l'entreprise.">
        <div className="form-row">
          <TextField
            label="Titre de la section"
            value={values.title}
            onChange={(v) => setSection('values', { title: v })}
          />
          <TextField
            label="Sous-titre"
            value={values.subtitle}
            onChange={(v) => setSection('values', { subtitle: v })}
          />
        </div>
        <RepeatableList
          items={values.items}
          onChange={(items) => setSection('values', { items })}
          itemLabel="Valeur"
          addLabel="Ajouter une valeur"
          newItem={() => ({ icon: 'target', title: '', description: '', color: 'blue' })}
          renderItem={(item, update) => (
            <>
              <div className="form-row">
                <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
                <SelectField
                  label="Icône"
                  value={item.icon}
                  onChange={(v) => update({ icon: v })}
                  options={ICON_OPTIONS}
                />
                <SelectField
                  label="Couleur"
                  value={item.color}
                  onChange={(v) => update({ color: v })}
                  options={VALUE_COLORS}
                />
              </div>
              <TextAreaField
                label="Description"
                value={item.description}
                onChange={(v) => update({ description: v })}
                rows={2}
              />
            </>
          )}
        />
      </Section>

      {/* ------------------------------------------------------ Timeline */}
      <Section title="Notre parcours" description="La frise chronologique des étapes clés.">
        <div className="form-row">
          <TextField
            label="Titre de la section"
            value={timeline.title}
            onChange={(v) => setSection('timeline', { title: v })}
          />
          <TextField
            label="Sous-titre"
            value={timeline.subtitle}
            onChange={(v) => setSection('timeline', { subtitle: v })}
          />
        </div>
        <RepeatableList
          items={timeline.items}
          onChange={(items) => setSection('timeline', { items })}
          itemLabel="Étape"
          addLabel="Ajouter une étape"
          newItem={() => ({ year: '', title: '', description: '' })}
          renderItem={(item, update) => (
            <>
              <div className="form-row">
                <TextField
                  label="Année"
                  value={item.year}
                  onChange={(v) => update({ year: v })}
                  placeholder="ex: 2024"
                />
                <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              </div>
              <TextAreaField
                label="Description"
                value={item.description}
                onChange={(v) => update({ description: v })}
                rows={2}
              />
            </>
          )}
        />
      </Section>

      {/* -------------------------------------------------- Achievements */}
      <Section
        title="Ce qui nous distingue"
        description="La liste des atouts et l'encadré rouge « Pourquoi nous choisir »."
      >
        <TextField
          label="Titre de la section"
          value={achievements.title}
          onChange={(v) => setSection('achievements', { title: v })}
        />
        <TextAreaField
          label="Introduction"
          value={achievements.intro}
          onChange={(v) => setSection('achievements', { intro: v })}
          rows={2}
        />
        <StringList
          label="Atouts (liste à puces)"
          items={achievements.items}
          onChange={(items) => setSection('achievements', { items })}
          addLabel="Ajouter un atout"
        />

        <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #e2e8f0' }} />

        <TextField
          label="Encadré — titre"
          value={achievements.card_title}
          onChange={(v) => setSection('achievements', { card_title: v })}
        />
        <RepeatableList
          items={achievements.card_items}
          onChange={(items) => setSection('achievements', { card_items: items })}
          itemLabel="Argument"
          addLabel="Ajouter un argument"
          newItem={() => ({ title: '', description: '' })}
          renderItem={(item, update) => (
            <div className="form-row" style={{ marginBottom: 0 }}>
              <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              <TextField
                label="Description"
                value={item.description}
                onChange={(v) => update({ description: v })}
              />
            </div>
          )}
        />
      </Section>

      {/* ---------------------------------------------------------- Team */}
      <Section title="Notre équipe" description="Les membres affichés en bas de page.">
        <CheckboxField
          label="Afficher la section équipe sur le site"
          checked={team.enabled !== false}
          onChange={(v) => setSection('team', { enabled: v })}
        />
        <div className="form-row">
          <TextField
            label="Titre de la section"
            value={team.title}
            onChange={(v) => setSection('team', { title: v })}
          />
          <TextField
            label="Sous-titre"
            value={team.subtitle}
            onChange={(v) => setSection('team', { subtitle: v })}
          />
        </div>
        <RepeatableList
          items={team.members}
          onChange={(members) => setSection('team', { members })}
          itemLabel="Membre"
          addLabel="Ajouter un membre"
          newItem={() => ({ name: '', role: '', image: '' })}
          renderItem={(item, update) => (
            <>
              <div className="form-row">
                <TextField label="Nom" value={item.name} onChange={(v) => update({ name: v })} />
                <TextField label="Fonction" value={item.role} onChange={(v) => update({ role: v })} />
              </div>
              <ImageField
                label="Photo (optionnelle)"
                value={item.image}
                onChange={(v) => update({ image: v })}
                onError={setError}
                hint="Sans photo, une icône neutre est affichée. Format carré conseillé."
              />
            </>
          )}
        />
      </Section>

      {/* ----------------------------------------------------------- CTA */}
      <Section title="Appel à l'action" description="Le bloc rouge en bas de page.">
        <TextField label="Titre" value={cta.title} onChange={(v) => setSection('cta', { title: v })} />
        <TextAreaField
          label="Sous-titre"
          value={cta.subtitle}
          onChange={(v) => setSection('cta', { subtitle: v })}
          rows={2}
        />
        <div className="form-row">
          <TextField
            label="Bouton principal — texte"
            value={cta.primary_label}
            onChange={(v) => setSection('cta', { primary_label: v })}
          />
          <TextField
            label="Bouton principal — lien"
            value={cta.primary_url}
            onChange={(v) => setSection('cta', { primary_url: v })}
            placeholder="/products"
          />
        </div>
        <div className="form-row">
          <TextField
            label="Bouton secondaire — texte"
            value={cta.secondary_label}
            onChange={(v) => setSection('cta', { secondary_label: v })}
          />
          <TextField
            label="Bouton secondaire — lien"
            value={cta.secondary_url}
            onChange={(v) => setSection('cta', { secondary_url: v })}
            placeholder="/contact"
          />
        </div>
      </Section>

      <div className="pc-save-bar">
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
        </button>
      </div>
    </div>
  );
}

export default AboutPageContent;
